import React from 'react';
import { FaUserCheck, FaQuestionCircle, FaCalendarAlt, FaBell, FaRedo, FaPhone } from 'react-icons/fa';
import { PhoneButton, ButtonText } from './HeroSectionStyles';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  ProcessSection,
  ProcessTitle,
  ProcessFlow,
  StepContainer,
  StepIconContainer,
  StepIcon,
  StepContent,
  StepTitle,
  StepDescription,
  DataParticle,
  TechCircle,
  StepCorner,
  DataPoint
} from './ProcessFlowStyles';

// Container para o botão de telefone dentro do fluxo
const FlowPhoneButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2.5rem auto 1rem;
  width: 100%;
  text-align: center;
  
  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

// Texto do botão centralizado
const AdjustedButtonText = styled(ButtonText)`
  position: relative;
  left: 0; /* Removido o deslocamento para garantir centralização perfeita */
  text-align: center;
  margin: 1rem auto 0;
`;

const ProcessFlowSection = ({ onOpenModal }) => {
  // Verificar se estamos na rota /ty
  const location = useLocation();
  const isThankYouPage = location.pathname === '/ty';
  
  // Dados das etapas do processo
  const processSteps = [
    {
      icon: <FaUserCheck />,
      title: "Qualificação",
      description: "Perguntas qualificatórias definidas por você, filtrando leads qualificados."
    },
    {
      icon: <FaQuestionCircle />,
      title: "Esclarecimento",
      description: "Espaço para o lead tirar todas as dúvidas sobre a oferta."
    },
    {
      icon: <FaCalendarAlt />,
      title: "Agendamento",
      description: "Captura do melhor dia e horário para o lead e confirmação do agendamento."
    },
    {
      icon: <FaBell />,
      title: "Lembretes",
      description: "Envio de lembretes 24h antes e na manhã do agendamento."
    },
    {
      icon: <FaRedo />,
      title: "Follow Up",
      description: "Reativação de leads que não agendaram via ligação e WhatsApp."
    }
  ];

  // Função para gerar partículas de dados aleatórias
  const renderDataParticles = (count, containerIndex) => {
    const particles = [];
    const colors = ['#00FFFF', '#64B6FF', '#0080FF', '#FFFFFF'];
    
    for (let i = 0; i < count; i++) {
      const top = `${Math.random() * 100}%`;
      const left = `${Math.random() * 100}%`;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const duration = `${1 + Math.random() * 2}s`;
      const delay = `${Math.random() * 2}s`;
      
      particles.push(
        <DataParticle 
          key={`particle-${containerIndex}-${i}`}
          top={top}
          left={left}
          color={color}
          duration={duration}
          delay={delay}
        />
      );
    }
    
    return particles;
  };

  // Função para gerar círculos tecnológicos decorativos
  const renderTechCircles = () => {
    const circles = [];
    const sizes = ['15px', '20px', '25px', '30px', '40px'];
    const positions = [
      { top: '10%', left: '5%' },
      { top: '20%', left: '80%' },
      { top: '40%', left: '90%' },
      { top: '60%', left: '5%' },
      { top: '80%', left: '85%' },
      { top: '90%', left: '20%' }
    ];
    
    positions.forEach((pos, index) => {
      const size = sizes[Math.floor(Math.random() * sizes.length)];
      const opacity = (0.2 + Math.random() * 0.3).toFixed(2);
      const duration = `${3 + Math.random() * 3}s`;
      const delay = `${3 + Math.random() * 2}s`;
      
      circles.push(
        <TechCircle 
          key={`circle-${index}`}
          top={pos.top}
          left={pos.left}
          size={size}
          opacity={opacity}
          duration={duration}
          delay={delay}
        />
      );
    });
    
    return circles;
  };

  return (
    <ProcessSection>
      <ProcessTitle>FLUXO DE ATENDIMENTO</ProcessTitle>
      
      {/* Círculos tecnológicos decorativos */}
      {renderTechCircles()}
      
      <ProcessFlow>
        {processSteps.map((step, index) => (
          <StepContainer key={index} index={index}>
            <StepIconContainer>
              <StepIcon>{step.icon}</StepIcon>
              {/* Número da etapa dentro do ícone */}
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 32, 64, 0.9)',
                border: '1px solid rgba(0, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Orbitron', sans-serif",
                fontSize: '0.7rem',
                fontWeight: 'bold',
                color: '#00FFFF',
                zIndex: 3,
                boxShadow: '0 0 8px rgba(0, 255, 255, 0.5)'
              }}>
                {index + 1}
              </div>
              {renderDataParticles(5, index)}
            </StepIconContainer>
            
            <StepContent index={index}>
              {/* Cantos tecnológicos */}
              <StepCorner className="top-left" />
              <StepCorner className="top-right" />
              <StepCorner className="bottom-left" />
              <StepCorner className="bottom-right" />
              
              {/* Pontos de dados animados */}
              <DataPoint className="top-left" index={index} />
              <DataPoint className="top-right" index={index} />
              <DataPoint className="bottom-left" index={index} />
              <DataPoint className="bottom-right" index={index} />
              
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepContent>
          </StepContainer>
        ))}
      </ProcessFlow>
      
      {/* Botão de Teste Grátis dentro do fluxo de atendimento - não exibido na página de agradecimento */}
      {!isThankYouPage && onOpenModal && (
        <FlowPhoneButtonContainer>
          <div>
            <PhoneButton onClick={() => onOpenModal(false)}>
              <FaPhone />
            </PhoneButton>
            <AdjustedButtonText>Teste grátis</AdjustedButtonText>
          </div>
        </FlowPhoneButtonContainer>
      )}
    </ProcessSection>
  );
};

export default ProcessFlowSection;
